import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Shield, Scale, CreditCard, Gift, RefreshCw, Phone, Mail, MapPin } from 'lucide-react';

const TermsPageNew = () => {
  const navigate = useNavigate();

  const sections = [
    {
      id: 'intro',
      title: '1. Introduction',
      icon: FileText,
      content: (
        <p>
          Welcome to Mint a Milli, a gold booking platform operated by Mothers Gold &amp; Diamonds LLP. By using our services, you agree to these Terms and Conditions.
        </p>
      )
    },
    {
      id: 'definitions',
      title: '2. Definitions',
      icon: Scale,
      content: (
        <ul className="list-disc pl-5 space-y-2">
          <li>&quot;Milli&quot; refers to 0.001 grams (1/1000th of a gram) of 22KT gold.</li>
          <li>&quot;Booking&quot; refers to the advance reservation of Milli units at the prevailing Booking Rate.</li>
          <li>&quot;Refund&quot; refers to liquidating your Milli units back at the prevailing Refund Rate.</li>
          <li>&quot;Redemption&quot; refers to converting Milli units to a physical gold voucher.</li>
        </ul>
      )
    },
    {
      id: 'eligibility',
      title: '3. Eligibility & KYC',
      icon: Shield,
      content: (
        <ul className="list-disc pl-5 space-y-2">
          <li>Users must be at least 18 years old and possess a valid Indian phone number.</li>
          <li>Aadhaar KYC verification is mandatory before booking, refunding or redeeming gold.</li>
          <li>Each Aadhaar can be linked to only one active account. Verification will be refused if the Aadhaar is already linked to another active account.</li>
          <li>Once KYC is verified, your name and address are locked to the verified details and can only be changed by contacting support.</li>
          <li>PAN verification is mandatory once your total bookings reach ₹2,00,000 (Two Lakhs). Further bookings are blocked until PAN is verified.</li>
          <li>You may add a nominee (name, relationship and optionally their Aadhaar number) in the KYC section.</li>
        </ul>
      )
    },
    {
      id: 'gold-rates',
      title: '4. Gold Rates',
      icon: Scale,
      content: (
        <ul className="list-disc pl-5 space-y-2">
          <li>The 22KT gold rate per gram is sourced from the All Kerala Gold &amp; Silver Merchants Association (AKGSMA) and refreshed every 30 minutes.</li>
          <li>Rates may be manually adjusted by the company when necessary.</li>
          <li>The rate applicable to your transaction is the rate shown at the time you confirm it.</li>
        </ul>
      )
    },
    {
      id: 'booking',
      title: '5. Booking',
      icon: CreditCard,
      content: (
        <ul className="list-disc pl-5 space-y-2">
          <li>Booking Rate (per Milli) = 22KT gold rate per gram ÷ 1000, rounded UP to the nearest 10 paise. Example: ₹12,795/gram ÷ 1000 = ₹12.795, rounded up to ₹12.80/Milli.</li>
          <li>Minimum booking amount is ₹100.</li>
          <li>Payments that are not completed are automatically marked as failed after 30 minutes. If money is debited but Millis are not credited, contact support with your transaction ID.</li>
        </ul>
      )
    },
    {
      id: 'refund',
      title: '6. Refund',
      icon: RefreshCw,
      content: (
        <ul className="list-disc pl-5 space-y-2">
          <li>Refund Rate (per Milli) = 98% of the 22KT gold rate per gram ÷ 1000, rounded DOWN to the nearest 10 paise. Example: 98% of ₹12,795 = ₹12,539.10 ÷ 1000 = ₹12.539, rounded down to ₹12.50/Milli.</li>
          <li>Minimum refund amount is 50 Millis.</li>
          <li>Refunds are processed within 24-48 hours to your registered bank account.</li>
          <li>A refund cannot be cancelled once initiated, as the Millis are deducted from your balance immediately.</li>
        </ul>
      )
    },
    {
      id: 'redemption',
      title: '7. Redemption',
      icon: Gift,
      content: (
        <ul className="list-disc pl-5 space-y-2">
          <li>Minimum redemption is 1000 Milli (1 gram of gold).</li>
          <li>Gold vouchers are valid for 30 days from the date of issue.</li>
          <li>Vouchers can be redeemed for 22KT gold jewellery at any Mothers Gold &amp; Diamonds LLP showroom. Gold is collected from the showroom only; home delivery is not available.</li>
          <li>No making charges are deducted from your gold weight on redemption.</li>
        </ul>
      )
    },
    {
      id: 'payments',
      title: '8. Payments',
      icon: CreditCard,
      content: (
        <p>
          All payments are processed via UPI through HDFC Bank&apos;s secure payment integration. We do not store any payment credentials or card details. All transactions are protected with bank-grade encryption.
        </p>
      )
    },
    {
      id: 'referrals',
      title: '9. Referral Rewards',
      icon: Gift,
      content: (
        <ul className="list-disc pl-5 space-y-2">
          <li>When a friend signs up with your referral code and completes their first booking, you receive a gold reward credited as Millis at that day&apos;s Booking Rate.</li>
          <li>The reward amount is set by the company and may change from time to time.</li>
        </ul>
      )
    },
    {
      id: 'disclaimer',
      title: '10. Disclaimer',
      icon: Shield,
      content: (
        <p>
          Mint a Milli is not an investment platform. Gold prices fluctuate and past performance does not guarantee future results. Users should make informed decisions.
        </p>
      )
    },
    {
      id: 'contact',
      title: '11. Contact',
      icon: Phone,
      content: (
        <div className="space-y-2">
          <p>For queries, contact us at:</p>
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-[#B8860B]" />
            <span>support@mothersgold.in</span>
          </div>
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4 text-[#B8860B]" />
            <span>+91 8137 830 916</span>
          </div>
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-[#B8860B]" />
            <span>Mothers Gold &amp; Diamonds LLP, Palakkad, Kerala, India</span>
          </div>
        </div>
      )
    }
  ];

  return (
    <div className="min-h-screen pb-8" data-testid="terms-page-new">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#004D40] to-[#00302B] px-4 py-4 sticky top-16 z-10 mx-2 rounded-lg shadow-lg">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <div>
            <h1 className="text-xl text-white font-semibold" style={{ fontFamily: 'Georgia, serif' }}>
              Terms & Conditions
            </h1>
            <p className="text-xs text-white/70">Last updated: November 2026</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Intro Card */}
        <div className="bg-white/90 backdrop-blur-sm rounded-xl border border-[#B8860B]/30 p-6 mb-6 shadow-lg">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#004D40] to-[#00302B] flex items-center justify-center">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-[#004D40]" style={{ fontFamily: 'Georgia, serif' }}>
                Mint a Milli Terms of Service
              </h2>
              <p className="text-sm text-[#B8860B]">Mothers Gold & Diamonds LLP</p>
            </div>
          </div>
          <p className="text-sm text-gray-600">
            Please read these terms carefully before using our gold booking platform. 
            By accessing or using Mint a Milli, you agree to be bound by these terms.
          </p>
        </div>

        {/* Sections */}
        <div className="space-y-4">
          {sections.map((section) => (
            <div 
              key={section.id}
              className="bg-white/90 backdrop-blur-sm rounded-xl border border-[#B8860B]/20 overflow-hidden shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex items-center gap-3 p-4 border-b border-[#B8860B]/10 bg-gradient-to-r from-[#F5E6C8]/50 to-transparent">
                <div className="w-8 h-8 rounded-lg bg-[#004D40]/10 flex items-center justify-center">
                  <section.icon className="w-4 h-4 text-[#004D40]" />
                </div>
                <h3 className="text-base font-semibold text-[#004D40]" style={{ fontFamily: 'Georgia, serif' }}>
                  {section.title}
                </h3>
              </div>
              <div className="p-4 text-sm text-gray-700 leading-relaxed">
                {section.content}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-xs text-[#B8860B]/70">
            © 2026 Mothers Gold & Diamonds LLP. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TermsPageNew;
